import { describeRuleset, type Constraint, type Ruleset } from '../lib/ruleset'
import { PRESETS } from '../lib/presets'

/**
 * Redigerare för tournaments.ruleset.
 *
 * Villkorstyperna speglar exakt dem som regelmotorn i databasen kan
 * (0005_rule_engine.sql). Läggs en ny typ till där måste den läggas till
 * här — annars går den att skriva men inte att välja. Motsatt håll är
 * ofarligt: databasen rapporterar okända villkor som fel i stället för att
 * tiga om dem.
 */

const CONSTRAINT_TYPES = [
  { value: 'region_exact', label: 'Exakt antal per region' },
  { value: 'region_min', label: 'Minst antal per region' },
  { value: 'max_top_n', label: 'Högst N spelare i topp X' },
  { value: 'min_rank_sum', label: 'Rankingsumma minst' },
  { value: 'max_rank_sum', label: 'Rankingsumma högst' },
  { value: 'min_distinct_countries', label: 'Minst antal olika länder' },
] as const

const TIEBREAKERS = [
  { value: 'total_to_par', label: 'Lägst totalscore' },
  { value: 'fewest_usa', label: 'Minst antal USA-spelare' },
  { value: 'best_nordic', label: 'Bästa nordiska placering' },
  { value: 'highest_rank_sum', label: 'Högst rankingsumma' },
] as const

const DEFAULT_PARAMS: Record<string, Record<string, number>> = {
  region_exact: { USA: 2, EUROPE: 2, ROW: 2 },
  region_min: { EUROPE: 1 },
  max_top_n: { n: 10, max: 2 },
  min_rank_sum: { min: 250 },
  max_rank_sum: { max: 900 },
  min_distinct_countries: { min: 6 },
}

interface Props {
  value: Ruleset
  onChange: (next: Ruleset) => void
}

export function RulesetEditor({ value, onChange }: Props) {
  const constraints = value.constraints ?? []

  const patch = (p: Partial<Ruleset>) => onChange({ ...value, ...p })

  const setConstraint = (i: number, c: Constraint) =>
    patch({ constraints: constraints.map((x, j) => (j === i ? c : x)) })

  const setParam = (i: number, key: string, n: number) => {
    const c = constraints[i]
    if (!c) return
    setConstraint(i, { ...c, params: { ...(c.params ?? {}), [key]: n } })
  }

  return (
    <div className="stack">
      <div>
        <label>Utgå från</label>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {PRESETS.map((p) => (
            <button
              key={p.key}
              type="button"
              title={p.note}
              onClick={() => onChange(structuredClone(p.ruleset))}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="muted">
          Reglerna sätts av den som kom sist i föregående major, så de ser sällan
          likadana ut. Presets är en startpunkt, inte facit.
        </p>
      </div>

      <div>
        <label htmlFor="team-size">Antal spelare per lag</label>
        <input
          id="team-size"
          type="number"
          min={1}
          max={12}
          value={value.team_size ?? 6}
          onChange={(e) => patch({ team_size: Number(e.target.value) })}
          style={{ maxWidth: 120 }}
        />
      </div>

      <div className="stack">
        <label>Villkor</label>

        {constraints.length === 0 && <p className="muted">Inga villkor — alla lag godkänns.</p>}

        {constraints.map((c, i) => (
          <div key={i} className="rule-row">
            <select
              value={c.type}
              onChange={(e) =>
                setConstraint(i, {
                  type: e.target.value,
                  params: structuredClone(DEFAULT_PARAMS[e.target.value] ?? {}),
                })
              }
            >
              {CONSTRAINT_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>

            <div className="rule-row__params">
              {Object.entries(c.params ?? {}).map(([key, n]) => (
                <label key={key} className="rule-param">
                  <span>{key}</span>
                  <input
                    type="number"
                    value={n}
                    onChange={(e) => setParam(i, key, Number(e.target.value))}
                  />
                </label>
              ))}
            </div>

            <button
              type="button"
              className="link"
              onClick={() => patch({ constraints: constraints.filter((_, j) => j !== i) })}
            >
              Ta bort
            </button>
          </div>
        ))}

        <div>
          <button
            type="button"
            onClick={() =>
              patch({
                constraints: [
                  ...constraints,
                  { type: 'max_top_n', params: structuredClone(DEFAULT_PARAMS.max_top_n!) },
                ],
              })
            }
          >
            Lägg till villkor
          </button>
        </div>
      </div>

      <div>
        <label>Cut-straff</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={value.cut_penalty?.mode ?? 'none'}
            onChange={(e) =>
              patch({
                cut_penalty:
                  e.target.value === 'graded'
                    ? { mode: 'graded', steps: value.cut_penalty?.steps?.length ? value.cut_penalty.steps : [5, 15, 30] }
                    : { mode: 'none', steps: [] },
              })
            }
          >
            <option value="none">Inget</option>
            <option value="graded">Graderat per lag</option>
          </select>

          {value.cut_penalty?.mode === 'graded' &&
            [0, 1, 2].map((i) => (
              <label key={i} className="rule-param">
                <span>{i + 1}{i === 2 ? '+' : ''} missad</span>
                <input
                  type="number"
                  value={value.cut_penalty?.steps?.[i] ?? 0}
                  onChange={(e) => {
                    const steps = [...(value.cut_penalty?.steps ?? [5, 15, 30])]
                    steps[i] = Number(e.target.value)
                    patch({ cut_penalty: { mode: 'graded', steps } })
                  }}
                />
              </label>
            ))}
        </div>
        <p className="muted">
          Spelaren behåller sin 36-hålsscore. Straffen läggs på laget ovanpå.
        </p>
      </div>

      <div>
        <label>WD / NTO</label>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={value.wd_penalty?.mode ?? 'cutline_plus'}
            onChange={(e) =>
              patch({
                wd_penalty:
                  e.target.value === 'cutline_plus'
                    ? { mode: 'cutline_plus', strokes: value.wd_penalty?.strokes ?? 5 }
                    : { mode: 'none', strokes: 0 },
              })
            }
          >
            <option value="cutline_plus">Cut-linjen plus straffslag</option>
            <option value="none">Inget</option>
          </select>

          {value.wd_penalty?.mode === 'cutline_plus' && (
            <label className="rule-param">
              <span>slag</span>
              <input
                type="number"
                value={value.wd_penalty?.strokes ?? 5}
                onChange={(e) =>
                  patch({ wd_penalty: { mode: 'cutline_plus', strokes: Number(e.target.value) } })
                }
              />
            </label>
          )}
        </div>
        <p className="muted">Räknas inte in i det graderade cut-straffet.</p>
      </div>

      <div>
        <label>Tiebreakers, i ordning</label>
        <div className="stack" style={{ gap: 6 }}>
          {(value.tiebreakers ?? []).map((tb, i) => (
            <div key={tb} className="rule-row">
              <span style={{ minWidth: 20, color: 'var(--gold)' }}>{i + 1}</span>
              <span style={{ flex: 1 }}>
                {TIEBREAKERS.find((x) => x.value === tb)?.label ?? tb}
              </span>
              <button
                type="button"
                className="link"
                disabled={i === 0}
                onClick={() => {
                  const next = [...(value.tiebreakers ?? [])]
                  const prev = next[i - 1]!
                  next[i - 1] = next[i]!
                  next[i] = prev
                  patch({ tiebreakers: next })
                }}
              >
                Upp
              </button>
              <button
                type="button"
                className="link"
                onClick={() =>
                  patch({ tiebreakers: (value.tiebreakers ?? []).filter((x) => x !== tb) })
                }
              >
                Ta bort
              </button>
            </div>
          ))}

          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {TIEBREAKERS.filter((t) => !(value.tiebreakers ?? []).includes(t.value)).map((t) => (
              <button
                key={t.value}
                type="button"
                onClick={() => patch({ tiebreakers: [...(value.tiebreakers ?? []), t.value] })}
              >
                + {t.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="card" style={{ background: 'rgba(0,0,0,.25)' }}>
        <label>Så här läses reglerna av deltagarna</label>
        <ul className="muted" style={{ paddingLeft: 18, marginTop: 6 }}>
          {describeRuleset(value).map((r) => (
            <li key={r}>{r}</li>
          ))}
        </ul>
      </div>
    </div>
  )
}
